import Sensor from './Sensor';
import { SENSOR, ORIENTATION } from './enum';
import detectFullscreenChange, { isInFullscreen } from '@shared/utils/detectFullscreenChange';
import { isSafari } from '@shared/utils/detectAppleDevice';

const matchMediaIsSupported = Boolean(window?.matchMedia('all')?.addEventListener);

const DEFAULT_MOBILE_VIEWPORT = 1023;

const resolveCapabilities = () => {
	return {
		orientation:         Boolean(screen.orientation),
		requestIdleCallback: Boolean(window.requestIdleCallback),
	};
};

class Device {
	config = {
		debug:     false,
		viewports: {
			mobile:  DEFAULT_MOBILE_VIEWPORT,
			desktop: null,
		},
		store: {
			commit:    null,
			mutations: {
			// [SENSOR.ORIENTATION]: null,
			// [SENSOR.SCREEN_PIXEL_RATIO]: null,
			// [SENSOR.TOUCH]: null,
			// [SENSOR.VIEWPORT]: null,
			// [SENSOR.WINDOW_HEIGHT]: null,
			// [SENSOR.WINDOW_WIDTH]: null,
			},
		},
		sensors: [
			SENSOR.FULLSCREEN,
			SENSOR.ORIENTATION,
			SENSOR.SCREEN_PIXEL_RATIO,
			SENSOR.TOUCH,
			SENSOR.VIEWPORT,
			SENSOR.WINDOW_HEIGHT,
			SENSOR.WINDOW_WIDTH,
			SENSOR.BREAKPOINT,
		],
	};

	sensors = {
		[SENSOR.SCREEN_PIXEL_RATIO]: {
			getValue: () => {
				return window.devicePixelRatio;
			},
			setup: ({ self }) => {
				self.update();
			},
			update: ({ self }) => {
				if (!matchMediaIsSupported) return;

				const value = self.getValue();
				matchMedia(`(resolution: ${value}dppx)`).addEventListener('change', self.update, { once: true });
				if (value !== self.cachedValue) self.setValue(value);
			},
		},
		[SENSOR.WINDOW_WIDTH]: {
			getValue: () => {
				return document.documentElement.clientWidth;
			},
			setup: ({ self }) => {
				window.addEventListener('resize', () => {
					const value = self.getValue();
					if (value !== self.cachedValue) self.setValue(value);
				});
			},
		},
		[SENSOR.WINDOW_HEIGHT]: {
			getValue: () => {
				return document.documentElement.clientHeight;
			},
			setup: ({ self }) => {
				window.addEventListener('resize', () => {
					const value = self.getValue();
					if (value !== self.cachedValue) self.setValue(value);
				});
			},
		},
		[SENSOR.ORIENTATION]: {
			getValue: ({ self }) => {
				return self.state?.media?.matches ? ORIENTATION.PORTRAIT : ORIENTATION.LANDSCAPE;
			},
			setup: ({ self }) => {
				if (!matchMediaIsSupported) {
					const portraitOrientations = ['portrait-primary', 'portrait-secondary'];
					const landscapeOrientation = ['landscape-primary', 'landscape-secondary'];
					const orientation = screen.orientation?.type;
					if (portraitOrientations.includes(orientation)) {
						self.setValue(ORIENTATION.PORTRAIT);
					} else if (landscapeOrientation.includes(orientation)) {
						self.setValue(ORIENTATION.LANDSCAPE);
					} else {
						self.setValue(ORIENTATION.PORTRAIT);
					}
					return;
				}
				self.state.media = matchMedia('(orientation: portrait)');
				self.state.media.addEventListener('change', self.update);
				self.update();
			},
			update: ({ self }) => {
				const value = self.getValue();
				if (value !== self.cachedValue) self.setValue(value);
			},
		},
		[SENSOR.FULLSCREEN]: {
			getValue: ({ self }) => {
				return self.state?.media?.matches;
			},
			setup: ({ self }) => {
				if (!matchMediaIsSupported || isSafari()) {
					detectFullscreenChange(() => {
						self.setValue(Boolean(isInFullscreen()));
					});
					return;
				}
				self.state.media = matchMedia('(display-mode: fullscreen)');
				self.state.media.addEventListener('change', self.update);
				self.update();
			},
			update: ({ self }) => {
				const value = self.getValue();
				if (value !== self.cachedValue) self.setValue(value);
			},
		},
		[SENSOR.TOUCH]: {
			getValue: ({ self }) => {
				return self.state?.media?.matches;
			},
			setup: ({ self }) => {
				if (!matchMediaIsSupported) return;
				self.state.media = matchMedia('(pointer: coarse)');
				self.state.media.addEventListener('change', self.update);
				self.update();
			},
			update: ({ self }) => {
				const value = self.getValue();
				if (value !== self.cachedValue) self.setValue(value);
			},
		},
		[SENSOR.VIEWPORT]: {
			getValue: ({ self }) => {
				const viewports = self.device.config.viewports || {};
				if (self.state && self.state.media) {
					for (const viewport of Object.keys(viewports)) {
						const media = self.state.media[viewport];
						if (media && media.matches) return viewport;
					}
				}

				return self.state.default;
			},
			setup: ({ self }) => {
				if (!matchMediaIsSupported) return;
				self.state.media = {};
				const viewports = self.device.config.viewports || {};
				for (const [viewport, size] of Object.entries(viewports)) {
					if (size === null) {
						self.state.default = viewport;
						continue;
					}
					const media = matchMedia(`(max-width: ${size}px)`);
					media.addEventListener('change', self.update);
					self.state.media[viewport] = media;
				}
				self.update();
			},
			update: ({ self }) => {
				const value = self.getValue();
				if (value !== self.cachedValue) self.setValue(value);
			},
		},
		[SENSOR.BREAKPOINT]: {
			getValue: () => {
				const indicator = document.querySelector('.breakpoint-indicator');
				if (!indicator) {
					return null;
				}
				return window.getComputedStyle(indicator, ':after').content.replace(/"/g, '');
			},
			setup: ({ self }) => {
				window.addEventListener('resize', () => {
					const value = self.getValue();
					if (value !== self.cachedValue) self.setValue(value);
				});
				self.update();
			},
			update: ({ self }) => {
				const value = self.getValue();
				if (value !== self.cachedValue) self.setValue(value);
			},
		},
	};

	supports = resolveCapabilities();

	constructor(config = {}) {
		this.config = { ...this.config, ...config };
		this.init();
	}

	init() {
		this.sensors = this.config.sensors.reduce((acc, id) => {
			const config = this.sensors[id];
			if (!config) return acc;
			acc[id] = new Sensor({ id, device: this, config });

			return acc;
		}, {});
	}

	emitSensorValue(sensor, value) {
		const { store } = this.config;
		if (store) {
			const mutation = store.mutations[sensor];
			if (mutation) store.commit(mutation, value);
		}
	}
}

export default Device;
